import React, { useCallback, useMemo } from 'react';
import { Square } from './Square';
import { Coordinates } from './Coordinates';
import { Promotion } from './Promotion';
import { useChessGame } from '@hooks/useChessGame';
import { ChessEngine } from '@services/ChessEngine';
import type { GameState, Position, PieceType } from '@/types';
import styles from './Board.module.css';

interface BoardProps {
  gameState: GameState;
  orientation: 'white' | 'black';
  onMove: (from: Position, to: Position, promotion?: PieceType) => void;
  disabled?: boolean;
  highlights?: Position[];
}

export const Board: React.FC<BoardProps> = ({
  gameState,
  orientation,
  onMove,
  disabled = false,
  highlights = [],
}) => {
  const {
    selectedSquare,
    legalMoves,
    handleSquareClick,
    isInCheck,
    getKingPosition,
  } = useChessGame();
  
  const [promotionSquare, setPromotionSquare] = React.useState<Position | null>(null);
  
  // Safety check for board data
  React.useEffect(() => {
    if (gameState?.board && !(gameState.board.squares instanceof Map)) {
      console.error('Board squares is not a Map!', gameState.board.squares);
    }
  }, [gameState]);
  
  const handleSquareClickInternal = useCallback((position: Position) => {
    console.log('[Board] Square clicked:', position);
    if (disabled) {
      console.log('[Board] Board is disabled');
      return;
    }
    
    const move = handleSquareClick(position);
    console.log('[Board] handleSquareClick returned:', move);
    if (move) {
      // Check if this is a pawn promotion
      const piece = gameState.board.squares.get(ChessEngine.positionToKey(move.from));
      if (
        piece?.type === 'pawn' &&
        ((piece.color === 'white' && move.to.rank === 8) ||
         (piece.color === 'black' && move.to.rank === 1))
      ) {
        setPromotionSquare(move.to);
      } else {
        console.log('[Board] Calling onMove:', move.from, move.to);
        onMove(move.from, move.to);
      }
    }
  }, [disabled, handleSquareClick, gameState.board.squares, onMove]);
  
  const handlePromotion = useCallback((pieceType: PieceType) => {
    if (selectedSquare && promotionSquare) {
      onMove(selectedSquare, promotionSquare, pieceType);
      setPromotionSquare(null);
    }
  }, [selectedSquare, promotionSquare, onMove]);
  
  const squares = useMemo(() => {
    const result: React.ReactElement[] = [];
    const files = orientation === 'white' 
      ? ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h']
      : ['h', 'g', 'f', 'e', 'd', 'c', 'b', 'a'];
    const ranks = orientation === 'white'
      ? [8, 7, 6, 5, 4, 3, 2, 1]
      : [1, 2, 3, 4, 5, 6, 7, 8];
    
    // Safety check for board data
    if (!gameState?.board?.squares || !(gameState.board.squares instanceof Map)) {
      console.error('Board squares is not available or not a Map', {
        hasBoard: !!gameState?.board,
        hasSquares: !!gameState?.board?.squares,
        isMap: gameState?.board?.squares instanceof Map,
        squares: gameState?.board?.squares
      });
      // Return empty board
      ranks.forEach((rank, rankIdx) => {
        files.forEach((file, fileIdx) => {
          const position = { file, rank } as Position;
          const key = ChessEngine.positionToKey(position);
          const isLight = (rankIdx + fileIdx) % 2 === 0;
          
          result.push(
            <Square
              key={key}
              position={position}
              piece={undefined}
              isLight={isLight}
              isSelected={false}
              isLegalMove={false}
              isHighlighted={false}
              isLastMove={false}
              isCheck={false}
              onClick={handleSquareClickInternal}
            />
          );
        });
      });
      return result;
    }
    
    const kingPosition = isInCheck ? getKingPosition() : null;
    const lastMove = gameState.moveHistory[gameState.moveHistory.length - 1];
    
    ranks.forEach((rank, rankIdx) => {
      files.forEach((file, fileIdx) => {
        const position = { file, rank } as Position;
        const key = ChessEngine.positionToKey(position);
        const piece = gameState.board.squares.get(key);
        const isLight = (rankIdx + fileIdx) % 2 === 0;
        
        const isSelected = selectedSquare && 
          selectedSquare.file === file && 
          selectedSquare.rank === rank;
        
        const isLegalMove = legalMoves.some(
          move => move.file === file && move.rank === rank
        );
        
        const isHighlighted = highlights.some(
          pos => pos.file === file && pos.rank === rank
        );
        
        const isLastMove = !!(lastMove && (
          (lastMove.from.file === file && lastMove.from.rank === rank) ||
          (lastMove.to.file === file && lastMove.to.rank === rank)
        ));
        
        const isCheckSquare = !!(kingPosition && 
          kingPosition.file === file && 
          kingPosition.rank === rank);
        
        result.push(
          <Square
            key={key}
            position={position}
            piece={piece}
            isLight={isLight}
            isSelected={isSelected || false}
            isLegalMove={isLegalMove}
            isHighlighted={isHighlighted}
            isLastMove={isLastMove}
            isCheck={isCheckSquare || false}
            onClick={handleSquareClickInternal}
          />
        );
      });
    });
    
    return result;
  }, [
    orientation, 
    gameState, 
    selectedSquare, 
    legalMoves, 
    highlights, 
    isInCheck, 
    getKingPosition,
    handleSquareClickInternal
  ]);
  
  return (
    <div className={styles.container}>
      <Coordinates orientation={orientation} type="files" />
      <div className={styles.boardWrapper}>
        <Coordinates orientation={orientation} type="ranks" />
        <div className={styles.board}>
          {squares}
        </div>
      </div>
      
      {promotionSquare && (
        <Promotion
          color={gameState.currentTurn}
          onSelect={handlePromotion}
          onCancel={() => setPromotionSquare(null)}
        />
      )}
    </div>
  );
};